import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faShieldAlt, faClock, faSyncAlt, faLock } from '@fortawesome/free-solid-svg-icons';
import settingsAPI from '../../../services/settingsService';
import patientService from '../../../services/patientService';
import Table from '../../../components/Common/Table/Table';
import ActionButtons from '../../../components/Common/ActionButtons/ActionButtons';
import ListScreenSearchBar from '../../../components/Common/ListScreenSearchBar/ListScreenSearchBar';
import ListScreenPaginationBar from '../../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar';
import Heading from '../../../components/Common/Heading/Heading';
import './PatientSettings.css';

// --- Follow Up Template Component for Patient ---
const PatientFollowUpSetting = () => {
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(30);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({ name: '', template: '' });
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  };

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.getFollowUpTemplates();
      if (response.success) {
        setTemplates(response.data || []);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    let filtered = templates;
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = templates.filter(
        (t) =>
          t.name?.toLowerCase().includes(searchLower) ||
          t.template?.toLowerCase().includes(searchLower)
      );
    }
    setFilteredTemplates(filtered);
    setCurrentPage(1);
  }, [templates, searchTerm]);

  const handleAddTemplate = () => {
    setEditingTemplate(null);
    setFormData({ name: '', template: '' });
    setShowModal(true);
  };

  const handleEditTemplate = async (templateId) => {
    try {
      const response = await settingsAPI.getTemplateById(templateId);
      if (response.success) {
        setEditingTemplate(response.data);
        setFormData({
          name: response.data.name || '',
          template: response.data.template || ''
        });
        setShowModal(true);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to fetch template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }
    try {
      const response = await settingsAPI.deleteTemplate(templateId);
      if (response.success) {
        toast.success(response.message || 'Template deleted successfully');
        fetchTemplates();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to delete template');
    }
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.template.trim()) {
      toast.error('Name and template content are required');
      return;
    }

    try {
      const templateData = {
        tempId: editingTemplate?.id || null,
        name: formData.name,
        tempContent: formData.template
      };
      const response = await settingsAPI.storeFollowUpTemplate(templateData);
      if (response.success) {
        toast.success(
          editingTemplate
            ? 'Template updated successfully'
            : 'Template created successfully'
        );
        setShowModal(false);
        fetchTemplates();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to save template');
    }
  };

  const sortedFilteredTemplates = useMemo(() => {
    const list = [...filteredTemplates];
    const mul = sortConfig.direction === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      switch (sortConfig.key) {
        case 'id':
          return mul * ((Number(a.id) || 0) - (Number(b.id) || 0));
        case 'name':
          return mul * String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' });
        case 'template':
          return mul * String(a.template || '').localeCompare(String(b.template || ''), undefined, { sensitivity: 'base' });
        default:
          return 0;
      }
    });
    return list;
  }, [filteredTemplates, sortConfig]);

  const effectiveItemsPerPage =
    itemsPerPage === 'all' ? sortedFilteredTemplates.length || 1 : (Number(itemsPerPage) || 30);
  const paginatedTemplates =
    itemsPerPage === 'all'
      ? sortedFilteredTemplates
      : sortedFilteredTemplates.slice(
        (currentPage - 1) * effectiveItemsPerPage,
        currentPage * effectiveItemsPerPage
      );
  const totalPages = itemsPerPage === 'all' ? 1 : Math.ceil(sortedFilteredTemplates.length / effectiveItemsPerPage);

  const rowNumberForTemplateIndex = (rowIndex) => {
    const total = sortedFilteredTemplates.length;
    if (total === 0) return rowIndex + 1;
    const pos = (currentPage - 1) * effectiveItemsPerPage + rowIndex;
    if (sortConfig.key === 'id' && sortConfig.direction === 'desc') {
      return total - pos;
    }
    return pos + 1;
  };

  const columns = [
    {
      key: 'rowNum',
      sortKey: 'id',
      title: '#',
      sortable: true,
      className: 'reusable-table-centered-col',
      render: (_row, rowIndex) => rowNumberForTemplateIndex(rowIndex),
    },
    { key: 'name', title: 'Template Name', sortable: true, render: (row) => row.name || 'N/A' },
    {
      key: 'template',
      title: 'Content',
      sortable: true,
      className: 'follow-up-col-template',
      render: (row) => {
        const content = row.template || '';
        if (!content) return 'N/A';
        return <div className="follow-up-template-content-full">{content}</div>;
      },
    },
    {
      key: 'action',
      title: 'Action',
      className: 'reusable-table-centered-col',
      render: (row) => (
        <ActionButtons
          onEdit={() => handleEditTemplate(row.id)}
          onDelete={() => handleDeleteTemplate(row.id)}
          hideView
        />
      ),
    },
  ];

  return (
    <div className="patient-settings-follow-up-setting">
      <Heading title="Follow Up Template" level="h3" className="patient-settings-subsection-title" />
      <div className="patient-settings-follow-up-top-bar">
        <ListScreenSearchBar
          value={searchTerm}
          onChange={(value) => {
            setSearchTerm(value);
            setCurrentPage(1);
          }}
          inputId="patient-settings-follow-up-search"
        />
        <button className="list-screen-add-btn" onClick={handleAddTemplate}>
          Add Template
        </button>
      </div>

      <div className="patient-settings-table-card">
        <Table
          columns={columns}
          data={paginatedTemplates}
          sortBy={sortConfig.key}
          sortOrder={sortConfig.direction === 'asc' ? 'asc' : 'desc'}
          onSort={requestSort}
          isLoading={loading}
          loadingMode="skeleton"
          skeletonRows={Math.min(effectiveItemsPerPage, 10)}
          emptyMessage="No templates found"
        />
        <ListScreenPaginationBar
          entriesValue={String(itemsPerPage)}
          onEntriesChange={(val) => {
            setItemsPerPage(val === 'all' ? 'all' : Number(val));
            setCurrentPage(1);
          }}
          showPageSelector
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>

      {showModal && (
        <div
          className="practices-list-modal-overlay"
          onClick={() => setShowModal(false)}
        >
          <div
            className="practices-list-modal-container"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="practices-list-modal-header">
              <h5 className="practices-list-modal-title">
                {editingTemplate ? 'Edit Template' : 'Add Template'}
              </h5>
              <button
                type="button"
                className="practices-list-modal-close"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSaveTemplate}>
              <div className="practices-list-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#5c5c5c', textTransform: 'uppercase', marginBottom: '6px' }}>
                    Template Name <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-input-custom"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#5c5c5c', textTransform: 'uppercase', marginBottom: '6px' }}>
                    Template Content <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <textarea
                    value={formData.template}
                    onChange={(e) => setFormData({ ...formData, template: e.target.value })}
                    required
                    rows="8"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d8e0e6',
                      borderRadius: '6px',
                      fontFamily: 'inherit',
                      fontSize: '13px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
              <div className="practices-list-modal-footer">
                <button
                  type="button"
                  className="practices-list-btn-cancel"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="update-btn"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// --- Main Patient Settings Component ---
const PatientSettings = () => {
  const { user } = useSelector((state) => state.auth || {});
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  const [activeTab, setActiveTab] = useState('account');
  const [accountData, setAccountData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionTime, setSessionTime] = useState(60);
  const [twoWayAuthEnabled, setTwoWayAuthEnabled] = useState(false);

  useEffect(() => {
    updateBreadcrumbs([
      { label: 'Dashboard', path: '/patient-dashboard' },
      { label: 'Settings', path: '/patient-settings', active: true }
    ]);
    return () => {
      clearBreadcrumbs();
    };
  }, [updateBreadcrumbs, clearBreadcrumbs]);

  useEffect(() => {
    if (user && user.id) {
      fetchAccountSettings();
    }
  }, [user]);

  const fetchAccountSettings = async () => {
    try {
      setLoading(true);
      try {
        const settingsResponse = await settingsAPI.getUserAccountSettings();
        if (settingsResponse.success) {
          setAccountData(settingsResponse.data);
          setSessionTime(settingsResponse.data.session_time || 60);
          setTwoWayAuthEnabled(settingsResponse.data.is_two_way_auth === 1);
        }
      } catch (error) {
        console.log('Settings API fallback to patient details');
      }

      let practiceId = user.practice_id || (user.practices && user.practices[0]?.id);
      let patientId = user.patient_id;

      if (!patientId || !practiceId) {
        try {
          const response = await patientService.getCurrentPatient();
          if (response.data?.success && response.data.data) {
            const patient = response.data.data;
            practiceId = patient.practice_id;
            patientId = patient.id;
          }
        } catch (apiError) {
          console.error('Error fetching patient data:', apiError);
        }
      }

      if (practiceId && patientId) {
        const detailsResponse = await patientService.getPatientDetails(practiceId, patientId);

        if (detailsResponse.data?.success) {
          const fullData = detailsResponse.data.data;
          const patientInfo = fullData.patient || {};
          const fullName = `${patientInfo.first_name || ''} ${patientInfo.last_name || ''}`.trim();

          setAccountData(prev => ({
            ...prev,
            full_name: fullName || `${user.first_name || ''} ${user.last_name || ''}`.trim() || '-',
            username: user.username || '-',
            email: patientInfo.email || user.email || prev?.email || '-',
            phone: patientInfo.phone_number || prev?.phone || '-',
            fax: prev?.fax || '-',
            session_time: prev?.session_time || 60,
            is_two_way_auth: prev?.is_two_way_auth !== undefined ? prev.is_two_way_auth : (user.is_two_way_auth || 0)
          }));

          if (accountData?.is_two_way_auth === undefined) {
            setTwoWayAuthEnabled(user.is_two_way_auth === 1);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching account settings:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to fetch account settings');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSession = async () => {
    try {
      const response = await settingsAPI.updateSessionSettings(sessionTime);
      if (response.success) {
        toast.success(response.message || 'Session time updated successfully');
        fetchAccountSettings();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to update session time');
    }
  };

  const toggleTwoWayAuth = async () => {
    const newStatus = twoWayAuthEnabled ? 0 : 1;
    try {
      const response = await settingsAPI.toggleTwoWayAuth(newStatus);
      if (response.success) {
        toast.success(response.message || 'Two-way authentication updated');
        setTwoWayAuthEnabled(newStatus === 1);
        fetchAccountSettings();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to toggle two-way authentication');
    }
  };

  const handleClearCache = async () => {
    try {
      const response = await settingsAPI.clearCache();
      if (response.success) {
        toast.success(response.message || 'Cache cleared successfully');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to clear cache');
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'account':
        if (loading) {
          return <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>;
        }
        if (!accountData) {
          return <div style={{ textAlign: 'center', padding: '40px' }}>No data available</div>;
        }
        return (
          <div className="saas-settings-container">
            {/* Profile Information Section */}
            <div className="saas-settings-card">
              <div className="saas-card-header">
                <div className="saas-card-icon-title">
                  <FontAwesomeIcon icon={faUser} className="saas-card-icon" />
                  <div>
                    <h4 className="saas-card-title">Profile Information</h4>
                    <p className="saas-card-subtitle">Your personal account details and contact information.</p>
                  </div>
                </div>
              </div>
              <div className="saas-card-body">
                <div className="saas-info-grid">
                  <div className="saas-field-tile">
                    <span className="saas-field-label">User Name</span>
                    <div className="saas-field-value">{accountData.full_name || "-"}</div>
                  </div>
                  <div className="saas-field-tile">
                    <span className="saas-field-label">Username</span>
                    <div className="saas-field-value">{accountData.username || "-"}</div>
                  </div>
                  <div className="saas-field-tile">
                    <span className="saas-field-label">Email Address</span>
                    <div className="saas-field-value">{accountData.email || "-"}</div>
                  </div>
                  <div className="saas-field-tile">
                    <span className="saas-field-label">Phone Number</span>
                    <div className="saas-field-value">{accountData.phone || "-"}</div>
                  </div>
                  <div className="saas-field-tile">
                    <span className="saas-field-label">Fax</span>
                    <div className="saas-field-value">{accountData.fax || "-"}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Security & Authentication Section */}
            <div className="saas-settings-card">
              <div className="saas-card-header">
                <div className="saas-card-icon-title">
                  <FontAwesomeIcon icon={faShieldAlt} className="saas-card-icon" />
                  <div>
                    <h4 className="saas-card-title">Security & Authentication</h4>
                    <p className="saas-card-subtitle">Manage two-factor authentication and login verification.</p>
                  </div>
                </div>
              </div>
              <div className="saas-card-body">
                <div className="saas-setting-row">
                  <div className="saas-row-info">
                    <h5 className="saas-row-title">Two-Way Authentication</h5>
                    <p className="saas-row-description">Require verification for additional account access security.</p>
                  </div>
                  <div className="saas-row-action">
                    <button
                      type="button"
                      className={`saas-toggle-badge ${twoWayAuthEnabled ? "enabled" : "disabled"}`}
                      onClick={toggleTwoWayAuth}
                    >
                      <FontAwesomeIcon icon={faLock} className="me-1" />
                      {twoWayAuthEnabled ? "Enabled" : "Disabled"}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Session & Preferences Section */}
            <div className="saas-settings-card">
              <div className="saas-card-header">
                <div className="saas-card-icon-title">
                  <FontAwesomeIcon icon={faClock} className="saas-card-icon" />
                  <div>
                    <h4 className="saas-card-title">Session & Preferences</h4>
                    <p className="saas-card-subtitle">Configure session timeout limits and local cache preferences.</p>
                  </div>
                </div>
              </div>
              <div className="saas-card-body">
                <div className="saas-setting-row">
                  <div className="saas-row-info">
                    <h5 className="saas-row-title">Session Timeout Duration</h5>
                    <p className="saas-row-description">Set inactivity timeout limit in minutes (Current: <strong>{accountData.session_time || 60} mins</strong>).</p>
                  </div>
                  <div className="saas-row-action saas-session-action">
                    <input
                      type="number"
                      className="form-input-custom saas-session-input"
                      min={1}
                      value={sessionTime || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSessionTime(val === "" ? "" : parseInt(val));
                      }}
                      placeholder="Minutes"
                    />
                    <button
                      type="button"
                      className="list-screen-add-btn"
                      onClick={() => {
                        const timeToUpdate = Number(sessionTime);
                        if (!timeToUpdate || timeToUpdate < 1) {
                          toast.error("Please enter a valid session time (min 1 minute)");
                          return;
                        }
                        handleUpdateSession();
                      }}
                    >
                      Update
                    </button>
                  </div>
                </div>

                <div className="saas-setting-row border-top">
                  <div className="saas-row-info">
                    <h5 className="saas-row-title">System Cache</h5>
                    <p className="saas-row-description">Clear cached application data to reload updated permissions and data state.</p>
                  </div>
                  <div className="saas-row-action">
                    <button type="button" className="btn-secondary" onClick={handleClearCache}>
                      <FontAwesomeIcon icon={faSyncAlt} className="me-1" /> Refresh Cache
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'followup':
        return <PatientFollowUpSetting />;
      default:
        return null;
    }
  };

  return (
    <div className="patient-settings-page-wrapper">
      <Heading title="Settings" level="h2" className="patient-settings-page-title list-screen-title-text" />
      <div className="patient-settings-card">
        <div className="patient-settings-tabs" role="tablist">
          <button
            className={`patient-settings-tab ${activeTab === 'account' ? 'active' : ''}`}
            onClick={() => setActiveTab('account')}
          >
            Account Setting
          </button>
          <button
            className={`patient-settings-tab ${activeTab === 'followup' ? 'active' : ''}`}
            onClick={() => setActiveTab('followup')}
          >
            Follow Up Setting
          </button>
        </div>
        <div className="patient-settings-content">{renderContent()}</div>
      </div>
    </div>
  );
};

export default PatientSettings;
